import Component from '@glimmer/component'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import TooltipCell from 'teacher-ui-client/components/stable-table/basic-cells/tooltip'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { SafeString } from '@ember/template'

/**
 * Provide this component with rows of labels and values to make a progression type table
 *
 * headerLabel: 'Progress'
 * tableData: [
 *  { label: 'Initial Peak', value: 1 },
 *  { label: 'Total Peaks Completed', value: 2, tooltip: 'My Tooltip', isHighlighted: true, isBold: true },
 * ]
 *
 * Renders a table like this;
 *
 * Progress____________________
 * Initial Peak          |  1
 * Total Peaks Completed |  2
 */

interface Args {
  theme?: ThemeKeys
  headerLabel?: string
  tableData: TableDataItem[]
}

interface Signature {
  Args: Args
  Element: HTMLTableElement
}

export interface TableDataItem {
  label: string
  value?: number | string | SafeString
  tooltip?: string
  isHighlighted?: boolean
  isBold?: boolean
}

interface LabelCellItem {
  value: string
  component?: typeof TooltipCell
  tooltipValue?: string
}

interface Theme {
  border: string
  highlightBg: string
}
const themes: Partial<Record<ThemeKeys, Theme>> = {
  [ThemeKeys.blue]: { border: 'border-oceany-blue-150', highlightBg: 'bg-oceany-blue-100' },
  [ThemeKeys.green]: { border: 'border-ms-green-150', highlightBg: 'bg-ms-green-100' },
  [ThemeKeys.default]: { border: 'border-black', highlightBg: 'bg-dusty-black-100' },
}

export class StudentDashboardProgressionTable extends Component<Signature> {
  get selectedTheme() {
    const theme = this.args.theme ?? ThemeKeys.default
    return themes[theme]
  }

  get columns() {
    const borderClass = this.selectedTheme?.border

    const defaultColumn = {
      classNames: `table-box-header border-b-4 ${borderClass} print:border-black`,
      cellClassNames: `table-box-cell border-b ${borderClass} print:border-black`,
    }

    const columns = [
      {
        ...defaultColumn,
        value: this.args.headerLabel,
      },
      {
        ...defaultColumn,
        value: '',
        classNames: `${defaultColumn.classNames} text-center w-32`,
        cellClassNames: `${defaultColumn.cellClassNames} text-center w-32`,
      },
    ]
    return columns
  }

  get rows() {
    const highlightBgClass = this.selectedTheme?.highlightBg
    const boldClass = 'font-semibold'

    const rows = this.args.tableData.map((dataRow) => {
      const labelCell: LabelCellItem = { value: dataRow.label }
      const dataCell = { value: dataRow.value }
      if (dataRow.tooltip) {
        labelCell.component = TooltipCell
        labelCell.tooltipValue = dataRow.tooltip
      }

      const rowClassNames = `${dataRow.isBold ? boldClass : ''} ${dataRow.isHighlighted ? highlightBgClass : ''}`
      const baseRow = { classNames: rowClassNames, cells: [labelCell, dataCell] }

      return baseRow
    })

    return rows
  }

  <template>
    <BasicTable class="w-full" @columns={{this.columns}} @rows={{this.rows}} ...attributes as |bt|>
      <bt.head />
      <bt.body />
    </BasicTable>
  </template>
}

export default StudentDashboardProgressionTable
